/**
 * Planner Types - Shared types for the Week Planner components
 */

// ============================================================================
// Curriculum Types
// ============================================================================

export interface CurriculumOutcome {
  id: string;
  subject: string;
  grade: string;
  organizing_idea: string;
  guiding_question: string;
  outcome_text: string;
  knowledge: string[];
  understanding: string[];
  skills: string[];
}

// ============================================================================
// Resource Types
// ============================================================================

export type ResourceType = "worksheet" | "pdf" | "link" | "note";

export interface Resource {
  id: string;
  type: ResourceType;
  title: string;
  url?: string;
  worksheetId?: string;
}

// ============================================================================
// Time Block Types
// ============================================================================

export interface TimeBlock {
  id: string;
  day: DayOfWeek;
  startTime: string; // HH:mm format (e.g., "08:57")
  endTime: string; // HH:mm format (e.g., "09:45")
  subject: Subject | null;
  title: string;
  notes: string;
  outcomeIds: string[];
  resources: Resource[];
}

// ============================================================================
// Week / Plan Types
// ============================================================================

export type DayOfWeek = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday";

export const DAYS_OF_WEEK: DayOfWeek[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

export const DAY_ABBREVIATIONS: Record<DayOfWeek, string> = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
};

/**
 * Map string DayOfWeek to numeric index (0-4)
 */
export const DAY_TO_INDEX: Record<DayOfWeek, number> = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
};

/**
 * Map numeric index (0-4) to string DayOfWeek
 */
export const INDEX_TO_DAY: DayOfWeek[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
];

/**
 * Convert a DayOfWeek string to its numeric index (0-4)
 */
export function dayToIndex(day: DayOfWeek): number {
  return DAY_TO_INDEX[day];
}

/**
 * Convert a numeric index (0-4) to DayOfWeek string
 */
export function indexToDay(index: number): DayOfWeek {
  if (index < 0 || index > 4) {
    throw new Error(`Invalid day index: ${index}. Must be 0-4.`);
  }
  return INDEX_TO_DAY[index];
}

export interface WeekPlan {
  id: string;
  weekStart: string; // ISO date string for Monday
  classId?: string;
  blocks: TimeBlock[];
}

// ============================================================================
// Subject Types
// ============================================================================

export type Subject =
  | "Math"
  | "English Language Arts"
  | "Social Studies"
  | "Science"
  | "French"
  | "Health"
  | "Physical Education"
  | "Art"
  | "Music"
  | "Other";

export const SUBJECTS: Subject[] = [
  "Math",
  "English Language Arts",
  "Social Studies",
  "Science",
  "French",
  "Health",
  "Physical Education",
  "Art",
  "Music",
  "Other",
];

export const SUBJECT_COLORS: Record<Subject, string> = {
  Math: "bg-blue-100 border-blue-300 text-blue-800",
  "English Language Arts": "bg-purple-100 border-purple-300 text-purple-800",
  "Social Studies": "bg-amber-100 border-amber-300 text-amber-800",
  Science: "bg-green-100 border-green-300 text-green-800",
  French: "bg-red-100 border-red-300 text-red-800",
  Health: "bg-pink-100 border-pink-300 text-pink-800",
  "Physical Education": "bg-orange-100 border-orange-300 text-orange-800",
  Art: "bg-indigo-100 border-indigo-300 text-indigo-800",
  Music: "bg-cyan-100 border-cyan-300 text-cyan-800",
  Other: "bg-gray-100 border-gray-300 text-gray-800",
};

export const SUBJECT_DOT_COLORS: Record<Subject, string> = {
  Math: "bg-blue-500",
  "English Language Arts": "bg-purple-500",
  "Social Studies": "bg-amber-500",
  Science: "bg-green-500",
  French: "bg-red-500",
  Health: "bg-pink-500",
  "Physical Education": "bg-orange-500",
  Art: "bg-indigo-500",
  Music: "bg-cyan-500",
  Other: "bg-gray-500",
};

// ============================================================================
// Default Time Slots (common Alberta school schedule)
// ============================================================================

export interface TimeSlot {
  label: string;
  startTime: string;
  endTime: string;
}

export const DEFAULT_TIME_SLOTS: TimeSlot[] = [
  { label: "Block 1", startTime: "08:45", endTime: "09:30" },
  { label: "Block 2", startTime: "09:30", endTime: "10:15" },
  { label: "Recess", startTime: "10:15", endTime: "10:30" },
  { label: "Block 3", startTime: "10:30", endTime: "11:15" },
  { label: "Block 4", startTime: "11:15", endTime: "12:00" },
  { label: "Lunch", startTime: "12:00", endTime: "12:45" },
  { label: "Block 5", startTime: "12:45", endTime: "13:30" },
  { label: "Block 6", startTime: "13:30", endTime: "14:15" },
  { label: "Recess", startTime: "14:15", endTime: "14:30" },
  { label: "Block 7", startTime: "14:30", endTime: "15:15" },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format time string for display (e.g., "08:45" -> "8:45 AM")
 */
export function formatTime(time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

/**
 * Get the Monday of the week containing the given date
 */
export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Format a date range for display (e.g., "Jan 20-24, 2026")
 */
export function formatWeekRange(weekStart: Date): string {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 4);

  const startMonth = weekStart.toLocaleDateString("en-US", { month: "short" });
  const endMonth = weekEnd.toLocaleDateString("en-US", { month: "short" });
  const startDay = weekStart.getDate();
  const endDay = weekEnd.getDate();
  const year = weekStart.getFullYear();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay}-${endDay}, ${year}`;
  }
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
}

/**
 * Generate an array of dates for the week
 */
export function getWeekDates(weekStart: Date): Date[] {
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
}
